export default class GameGroupHandler {
    constructor(game) {
        this.game = game;
        this.gameApi = game.gameApi;

        this.initialized = false;
        this.connectionId = null;
        this.groupMembers = [];
        this.groupSeats = [];
        this.lobbyConfig = null;
        this.memberRecord = {};
        // Phase C migration repair (2026-05-21): owner identity is the
        // Lobby's owning User, surfaced by the backend on every Members /
        // Join broadcast as data.owner_user_id. Stash here so render
        // surfaces (Lobby.js, PlayMenu.js) can derive isGroupLeader
        // without reaching for the dropped UserConnection.group self-FK.
        this._ownerUserId = null;
        // CC_OVERLAY_OBSERVER_SECTION: the spectator roster the backend
        // emits on every Members / Join broadcast alongside seats
        // (group_router._make_group_membership_message → observers =
        // display names, observer_count = their tally). Stashed here so the
        // lobby overlay's observer section can seed from the cache on a
        // mid-game mount, the same way it seeds seats / config.
        this.observers = [];
        this.observerCount = 0;

        this.ROUTER = {
            Members: this.receiveGroupMembers,
            Launch: this.launchReceiver,
            Leave: this.leaveEvent,
            Join: this.receiveGroupMembers,
            Invite: this.invited,
            List: this.list,
            LobbyDestroyed: this.lobbyDestroyedReceiver,
            PeerLeftMidGame: this.peerLeftMidGameReceiver,
            PeerRejoinedMidGame: this.peerRejoinedMidGameReceiver,
            PeerDisconnectedMidGame: this.peerDisconnectedMidGameReceiver,
            OwnerDisconnectedMidGame: this.ownerDisconnectedMidGameReceiver,
            TabKicked: this.tabKickedReceiver,
            // CC_REQUEST_A_SEAT (2026-06-08): the PRIVATE-game recruitment
            // rail. SeatRequested → owner's approve/decline modal;
            // Approved → the requester re-fetches the board as a seated
            // player; Declined/Rejected → the requester's notice +
            // pending-state reset; Voided → the owner's "seat gone" notice.
            SeatRequested: this.seatRequestedReceiver,
            SeatRequestApproved: this.seatRequestApprovedReceiver,
            SeatRequestDeclined: this.seatRequestDeclinedReceiver,
            SeatRequestRejected: this.seatRequestRejectedReceiver,
            SeatRequestVoided: this.seatRequestVoidedReceiver,
        };
        window.addEventListener('group-event', this.receiveGroupMessage);
        window.addEventListener('connection-event', this.receiveConnectionMessage);
        // F16 (CC_LOBBY_SAVE_FINISH): re-run the group rendezvous on a WS
        // reconnect. make_connection_record re-opens the socket but joins
        // no Redis group, and firstTimeGroupFetch's `initialized` guard
        // blocks the re-fetch — so the board went quiet until a manual
        // refresh. Reset the guard and re-fetch on a reconnect open.
        window.addEventListener('ws-open-event', this.onSocketOpen);
    }

    onSocketOpen = (event) => {
        const reopen = event && event.detail && event.detail.reopen;
        if (!reopen) return;  // first open: firstTimeGroupFetch covers it
        console.info('[Group] ws reopened — re-running group rendezvous');
        this.initialized = false;
        this.firstTimeGroupFetch();
    };

    receiveConnectionMessage = (e) => {
        this.connectionId = e.detail;
        this.memberRecord = {id: this.connectionId};
        console.info('[Group] connection-event connectionId=%s',
            this.connectionId);
    };

    receiveGroupMessage = (e) => {
        const receivedMessage = e.detail;
        const packageType = receivedMessage.package
            && receivedMessage.package.type;
        const handler = this.ROUTER[packageType];
        if (typeof handler !== 'function') {
            console.warn('[Group] no handler for package type=%s'
                + ' — ignoring', packageType);
            return;
        }
        handler(receivedMessage);
    };

    peerLeftMidGameReceiver = (receivedMessage) => {
        // CC_LOBBY_GAME_CRUD (2026-05-27): backend targets the owner's
        // channel with this when a non-owner peer leaves a PLAYING
        // lobby. Dispatch a window CustomEvent so PeerLeftModal can
        // surface Replace / Save & Move On for the owner.
        const data = (receivedMessage.package
            && receivedMessage.package.data) || {};
        console.info('[Group] peer_left_mid_game user_id=%s'
            + ' display_name=%s seat=%s',
            data.user_id, data.display_name, data.seat);
        window.dispatchEvent(new CustomEvent('peer-left-mid-game-event',
            { detail: {
                user_id: data.user_id,
                display_name: data.display_name,
                seat: data.seat,
            }}));
    };

    peerRejoinedMidGameReceiver = (receivedMessage) => {
        // CC_MIDGAME_SEAT_PRESENCE (2026-06-08): the rejoin mirror of
        // peerLeftMidGameReceiver. Backend targets the owner's channel
        // with this when a non-owner peer who patiently LEFT a PLAYING
        // lobby comes back (a seat-keyed rejoin via from_platform). The
        // seat is no longer empty, so dispatch a window event that
        // PeerLeftModal consumes to drop that peer from its pending queue
        // (the owner's "they left — Replace with AI?" prompt dismisses).
        const data = (receivedMessage.package
            && receivedMessage.package.data) || {};
        console.info('[Group] peer_rejoined_mid_game user_id=%s'
            + ' display_name=%s seat=%s',
            data.user_id, data.display_name, data.seat);
        window.dispatchEvent(new CustomEvent('peer-rejoined-mid-game-event',
            { detail: {
                user_id: data.user_id,
                display_name: data.display_name,
                seat: data.seat,
            }}));
    };

    peerDisconnectedMidGameReceiver = (receivedMessage) => {
        // CC_PEER_LEFT_SILENT (2026-06-03): backend targets the owner's
        // channel with this when a non-owner peer's socket DROPS mid-game
        // (tab close / connection loss), as distinct from an explicit
        // Leave Game (PeerLeftMidGame). DISCORD_KNOWLEDGE §6.5 had this
        // as a known gap — the owner used to get no signal at all. The
        // seat stays patient; the owner just gets the disconnect-variant
        // modal (Wait / Replace / Save & Move On).
        const data = (receivedMessage.package
            && receivedMessage.package.data) || {};
        console.info('[Group] peer_disconnected_mid_game user_id=%s'
            + ' display_name=%s seat=%s',
            data.user_id, data.display_name, data.seat);
        window.dispatchEvent(new CustomEvent(
            'peer-disconnected-mid-game-event',
            { detail: {
                user_id: data.user_id,
                display_name: data.display_name,
                seat: data.seat,
            }}));
    };

    ownerDisconnectedMidGameReceiver = (receivedMessage) => {
        // CC_DISCONNECT_VISIBILITY (2026-06-07, Symptom B): the mirror of
        // peerDisconnectedMidGameReceiver. Backend GROUP-broadcasts this
        // when the lobby OWNER's socket drops mid-game (the owner-leg of
        // mark_disconnected). Previously a host drop was silent and the
        // peers were left on a frozen board, "eventually forced to the
        // main menu for no reason." Dispatch a window event so
        // OwnerDisconnectedModal can explain the silence. The seat stays
        // patient — the owner may reconnect; this only informs.
        const data = (receivedMessage.package
            && receivedMessage.package.data) || {};
        console.info('[Group] owner_disconnected_mid_game user_id=%s'
            + ' display_name=%s seat=%s',
            data.user_id, data.display_name, data.seat);
        window.dispatchEvent(new CustomEvent(
            'owner-disconnected-mid-game-event',
            { detail: {
                user_id: data.user_id,
                display_name: data.display_name,
                seat: data.seat,
            }}));
    };

    seatRequestedReceiver = (receivedMessage) => {
        // CC_REQUEST_A_SEAT: backend targets the OWNER's channel when an
        // observer requests a recruitable AI seat in a PRIVATE game.
        // Dispatch a window event so SeatRequestModal surfaces Yes/No.
        const data = (receivedMessage.package
            && receivedMessage.package.data) || {};
        console.info('[Group] seat_requested user_id=%s display_name=%s'
            + ' seat=%s', data.user_id, data.display_name, data.seat);
        window.dispatchEvent(new CustomEvent('seat-requested-event',
            { detail: {
                user_id: data.user_id,
                display_name: data.display_name,
                seat: data.seat,
                faction: data.faction,
                snohe: data.snohe,
                mid_turn: data.mid_turn,
            }}));
    };

    seatRequestApprovedReceiver = (receivedMessage) => {
        // CC_REQUEST_A_SEAT: backend targets the REQUESTER's channel after
        // the owner approves. The backend has already repointed the seat
        // and transitioned the connection IN_GAME; re-fetch the board so
        // the FE drops observer chrome and mounts as the seated player.
        const data = (receivedMessage.package
            && receivedMessage.package.data) || {};
        console.info('[Group] seat_request_approved game_id=%s seat=%s',
            data.game_id, data.seat);
        if (data.game_id != null && this.gameApi
                && typeof this.gameApi.getGame === 'function') {
            this.gameApi.getGame(data.game_id);
        }
        window.dispatchEvent(new CustomEvent('seat-request-approved-event',
            { detail: { game_id: data.game_id, seat: data.seat } }));
    };

    seatRequestDeclinedReceiver = (receivedMessage) => {
        // CC_REQUEST_A_SEAT: backend targets the REQUESTER's channel when
        // the owner declines (or the seat voided). The observer keeps
        // watching; SeatRequestPanel shows the polite notice + clears its
        // pending state so they may request again.
        const data = (receivedMessage.package
            && receivedMessage.package.data) || {};
        console.info('[Group] seat_request_declined message=%s',
            data.message);
        window.dispatchEvent(new CustomEvent('seat-request-declined-event',
            { detail: { message: data.message } }));
    };

    seatRequestRejectedReceiver = (receivedMessage) => {
        // CC_REQUEST_A_SEAT: backend typed-reject to the REQUESTER when a
        // request is invalid (not observing / already pending / seat not
        // recruitable). Never silent — SeatRequestPanel surfaces it.
        const data = (receivedMessage.package
            && receivedMessage.package.data) || {};
        console.info('[Group] seat_request_rejected code=%s', data.code);
        window.dispatchEvent(new CustomEvent('seat-request-rejected-event',
            { detail: { code: data.code } }));
    };

    seatRequestVoidedReceiver = (receivedMessage) => {
        // CC_REQUEST_A_SEAT: backend targets the OWNER's channel when an
        // approve can no longer be honored (requester gone / seat filled).
        // Surfaces the owner's "no longer available" notice + clears the
        // pending modal for that requester.
        const data = (receivedMessage.package
            && receivedMessage.package.data) || {};
        console.info('[Group] seat_request_voided message=%s', data.message);
        window.dispatchEvent(new CustomEvent('seat-request-voided-event',
            { detail: { message: data.message } }));
    };

    tabKickedReceiver = (receivedMessage) => {
        // CC_LOBBY_SAVE_FINISH (2026-06-03), F2: backend sends this to the
        // OLD tab's channel when a second tab opens for the same user.
        // Dispatch a window event so TabKickedModal surfaces the
        // one-button "moved to a new window" modal and closes this socket.
        console.info('[Group] tab_kicked — a newer tab took the session');
        window.dispatchEvent(new CustomEvent('tab-kicked-event'));
    };

    lobbyDestroyedReceiver = (receivedMessage) => {
        const data = (receivedMessage.package
            && receivedMessage.package.data) || {};
        console.info('[Group] lobby_destroyed reason=%s lobbyId=%s'
            + ' departingUserId=%s',
            data.reason, data.group, data.departing_user_id);
        window.dispatchEvent(new CustomEvent('lobby-destroyed-event',
            { detail: {
                reason: data.reason,
                lobbyId: data.group,
                departingUserId: data.departing_user_id,
            }}));
    };

    isGroupLeader = () => {
        if (!this.memberRecord || !this.memberRecord.user) return false;
        if (this._ownerUserId === null
                || this._ownerUserId === undefined) {
            return false;
        }
        return this.memberRecord.user.id === this._ownerUserId;
    }

    receiveGroupMembers = (receivedMessage) => {
        const messagePackage = receivedMessage.package;
        const groupId = messagePackage.data.group;
        // CC_DISCORD_LOBBY_BULLETPROOF (2026-05-31, invariant I4): a
        // Members/Join payload with no group id means "you are not in a
        // lobby." Do NOT mount the Lobby surface or mutate lobby state —
        // the user belongs on the Main Menu. Backend no longer
        // auto-creates a lobby for a connection with none, so this is the
        // canonical not-in-a-lobby signal; treat it as a no-op.
        if (groupId === null || groupId === undefined) {
            console.warn('[Group] receiveGroupMembers with null group_id —'
                + ' staying on Main Menu, no lobby mount');
            return;
        }
        const members = messagePackage.data.members;
        const seats = messagePackage.data.seats || [];
        // CC_OVERLAY_OBSERVER_SECTION: the spectator roster the backend
        // emits alongside seats (observers = display names, observer_count =
        // their tally; both additive — see group_router). Surfaced on the
        // group-update-event detail so the lobby overlay's observer section
        // can render count + names. Empty list / 0 is the no-observers case.
        const observers = messagePackage.data.observers || [];
        const observerCount = (
            messagePackage.data.observer_count !== undefined
                ? messagePackage.data.observer_count
                : observers.length);
        const ownerUserId = (messagePackage.data.owner_user_id !== undefined
            ? messagePackage.data.owner_user_id : null);
        this._ownerUserId = ownerUserId;
        // CC_FE_GAPS (2026-06-01): the Members/Join payload now carries
        // the lobby uuid (backend group_router.py). Surface it on the
        // group-update-event detail so open-web New Game can route to
        // /game/:lobbyUuid off the next broadcast. Additive — the
        // integer `group`/groupId other consumers read is untouched.
        const lobbyUuid = (messagePackage.data.lobby_uuid !== undefined
            ? messagePackage.data.lobby_uuid : null);
        const lobbyConfig = {
            amenities: messagePackage.data.amenities,
            beds: messagePackage.data.beds,
            debris: messagePackage.data.debris,
            ghosts: messagePackage.data.ghosts,
            deck: messagePackage.data.deck,
            expansions: messagePackage.data.expansions,
            // CC_LOBBY_GAME_CRUD: Save & Exit / Abandon gating.
            status_id: messagePackage.data.status_id,
            current_game_id: messagePackage.data.current_game_id,
            // CC_LIVING_GAMES_LIST_DESIGN: the Members payload already
            // carries `public` (group_router._make_group_membership_message);
            // surface it so the in-game EscapeMenu public dial can render
            // the live state and flip it owner-only during PLAYING.
            public: messagePackage.data.public,
        };
        const seatUserIds = seats.map(
            (s) => (s && s.user && s.user.id) || null);
        console.info('[Group] receiveGroupMembers',
                    'lobbyId=', groupId,
                    'connectionId=', this.connectionId,
                    'ownerUserId=', ownerUserId,
                    'memberCount=', members && members.length,
                    'memberIds=', (members || []).map(m => m.id),
                    'seatCount=', seats.length,
                    'seatUserIds=', seatUserIds);
        console.log('[postgame-fp] receiveGroupMembers status_id=%s '
            + 'current_game_id=%s lobbyUuid=%s',
            messagePackage.data.status_id,
            messagePackage.data.current_game_id,
            lobbyUuid);
        if (!members || members.length === 0) {
            console.warn('[Group] zero length group — ' +
                         'keeping existing memberRecord', this.memberRecord);
            return;
        }

        this.groupMembers = members;
        this.groupSeats = seats;
        // CC_LOBBY_SEAT_CACHE_SEED (2026-06-05): stash the config block so
        // a Lobby that mounts AFTER this broadcast (the postgame
        // surface-flip race — the Members push that flips GamePage to the
        // lobby surface fires before Lobby's own listener exists) can seed
        // seats + status from the cache instead of rendering open seats.
        this.lobbyConfig = lobbyConfig;
        this.observers = observers;
        this.observerCount = observerCount;
        // CC_FEAI_LEADERSHIP_BY_USER_ID (G97, 2026-07-11): resolve "my
        // member record" by the STABLE user id first, not the volatile
        // connectionId. The connectionId is seeded ONLY by the one-shot
        // 'Connection' window event (backend async_consumers.py sends
        // type=Connection on accept). On a mid-game Main-Menu -> Resume
        // (a full page reload) that message can land before this handler's
        // connection-event listener has mounted, so connectionId stays null
        // forever — every Members payload then fails the id match, this
        // returns before establishing memberRecord.user, and isGroupLeader()
        // is permanently false. That switches OFF the owner-only FE-AI driver
        // (ArtificialPlayer.shouldAIUpdate's `empowered` gate), so every Feai
        // seat's turn stalls with no recovery — reloading re-runs the same
        // losing race. The user id is always available synchronously from the
        // account (Game.js sets game.userId) and is stable across reconnects
        // (one UserConnection row per user — sockets I1), so match on it. The
        // connectionId filter stays as a fallback for any pre-userId path.
        const myUserId = (this.game && this.game.userId != null)
            ? this.game.userId : null;
        let matchedMember = null;
        if (myUserId !== null) {
            matchedMember = members.find(
                (member) => member && member.user
                    && member.user.id === myUserId) || null;
        }
        if (!matchedMember) {
            matchedMember = members.filter(
                (member) => member.id === this.connectionId)[0] || null;
        }
        if (!matchedMember) {
            // Phase 0.5 (2026-04-11): If the lookup misses, the old code
            // silently set memberRecord = undefined and then crashed one
            // line later in isGroupLeader(). Log and bail rather than
            // poison state.
            console.warn('[Group] Members payload did not contain my ' +
                         'user id ' + myUserId + ' or connectionId ' +
                         this.connectionId +
                         ' — keeping existing memberRecord', this.memberRecord);
            return;
        }
        this.memberRecord = matchedMember;

        let owner = null;
        if (ownerUserId !== null && ownerUserId !== undefined) {
            owner = members.find(
                (m) => m && m.user && m.user.id === ownerUserId) || null;
        }
        if (owner === null) {
            owner = this.memberRecord;
        }
        window.dispatchEvent(new CustomEvent('group-update-event',
            {
                detail: {
                    members: this.groupMembers,
                    member: this.memberRecord,
                    isGroupLeader: this.isGroupLeader(),
                    owner: owner,
                    ownerUserId: ownerUserId,
                    lobbyUuid: lobbyUuid,
                    seats: this.groupSeats,
                    lobbyConfig: lobbyConfig,
                    observers: observers,
                    observerCount: observerCount,
                }
            }));
    };


    invited = (receivedMessage) => {
        const invitation = receivedMessage.package.data;
        window.dispatchEvent(
            new CustomEvent('game-invitation-event', { detail: invitation }));
    };

    list = (receivedMessage) => {
        // CC_RETIRE_LFG_TOGGLE (2026-06-07): Group/List is a friends-only
        // invite roster now — the retired `users`/lfg leg is gone.
        let friends = receivedMessage.package.data.friends;
        window.dispatchEvent(
            new CustomEvent(
                'invitables-event', {
                    detail: {
                        friends: friends,
                    }
                }));
    };

    firstTimeGroupFetch = () => {
        if (!this.initialized) {
            this.initialized = true;
            this.gameApi.fetchGroup();
        }
    };


    leaveGameGroup = () => {
        this.groupMembers = [];
        this.groupSeats = [];
        this.lobbyConfig = null;
        this.memberRecord = {};
        this._ownerUserId = null;
        this.observers = [];
        this.observerCount = 0;
        this.gameApi.leaveGroup();
        window.dispatchEvent(new Event('leave-group-event'));
    };

    leaveEvent = (receivedMessage) => {
        // The backend sends Group/Leave to this connection when:
        //   (a) self-leave was acknowledged (Leave Lobby button), or
        //   (b) we were kicked by the owner.
        //
        // CC_FE_PAGES (2026-06-01): route to '/' (Main Menu). The old
        // '/play/casual' target — and its "the backend has already
        // dispersed us into a fresh owned lobby" rationale — is stale
        // per the DISCORD_KNOWLEDGE §6.5 rewrite: leaving clears the
        // binding and lands the user on the Main Menu; the backend no
        // longer auto-rehomes anyone into a fresh lobby.
        window.dispatchEvent(
            new CustomEvent(
                'quick-router-event', { detail: '/' }));
    }

    launchGroupGame = (opts = {}) => {
        if (!this.isGroupLeader()) return;
        this.gameApi.sendLaunchPackage(opts);
    };

    launchReceiver = (receivedMessage) => {
        const packageData = receivedMessage.package.data;
        const gameId = packageData.game;
        const seat = packageData.seat;

        this.game.gameData.id = gameId;

        // Bind THIS connection's seat straight from the Launch payload.
        // engine.initializer._send_launch_message_to_player sends each
        // seated human their own assigned seat_index (AI seats have no
        // connection and receive nothing), so `seat` here is unambiguously
        // this client's own seat.
        //
        // Historically currentSeat/seatedController were nulled here and
        // re-established only when the board's sitCheck later fired a
        // take-seat → Sit roundtrip (gameSitEvent). That broke in the
        // Phase-C lobby-launch flow: the launcher claimed a *lobby* seat
        // pre-launch (claim_seat) and never Sat, so post-launch
        // dispatchTakeSeat routed the auto take-seat by the client's cached
        // lobbyConfig.status_id — still FORMING until a Members repaint
        // lands — and misrouted to claim_seat, which the server 409s
        // (wrong_status; the lobby left FORMING at launch). The Sit never
        // happened: currentSeat stayed null, seated-game-player-event never
        // fired, and the seated launcher was stranded as a spectator of
        // their own game (GamePage isSpectator = inGame && !gamePlayerData)
        // with a null seatedController — so no ChooseFaction API and a hard
        // stall on the faction ceremony, plus a claim_seat/409 loop on
        // every re-render.
        //
        // Binding from the authoritative Launch payload removes the race:
        // the next Game broadcast's per-seat game-player-event fan-out
        // matches currentSeat (Game.gamePlayerEvent), fires
        // seated-game-player-event to clear the spectator state, and flips
        // sitCheck's guard so the claim_seat loop stops. Mirrors exactly
        // what gameSitEvent binds, just earlier and from the launch signal.
        if (Number.isInteger(seat)
                && seat >= 0
                && seat < this.game.gamePlayerControllers.length) {
            this.game.currentSeat = seat;
            this.game.seatedController =
                this.game.gamePlayerControllers[seat];
        } else {
            this.game.currentSeat = null;
            this.game.seatedController = null;
        }

        // 2026-05-16: SpeedController.engaged persists across games
        // because the GamePlayerControllers (and their ArtificialPlayer
        // + SpeedController instances) are constructed once at Game
        // ctor, not per-game. Without an explicit reset, "Auto" stays
        // on between games. Rule: every new game starts with AI off.
        // Reset every seat's countdown / engagement state at launch.
        this.game.gamePlayerControllers.forEach((gpc) => {
            const sc = gpc.artificialPlayer.speedController;
            sc.stopCountdown();
            sc.engaged = false;
            sc.concuring = false;
        });

        // CC_FE_PAGES (2026-06-01): the `game-launch-event` is the
        // signal the /game/:lobbyUuid wrapper (GamePage) uses to source
        // the seat for the board (the URL no longer carries ?seat=).
        window.dispatchEvent(
            new CustomEvent(
                'game-launch-event',
                { detail: { game: gameId, seat: seat } }
            ));

        // Navigation is intentionally SUPPRESSED here. Every seated peer
        // is already on `/game/:lobbyUuid` (they arrived via the picker
        // tile / New Game), and GamePage re-branches Lobby→board in place
        // when the Game broadcast (`game-event`) lands — Members does not
        // re-fire after launch. Routing to a new URL here would only
        // fight the wrapper that already owns the right URL.
    }
}
